import Loader from 'components/Loader';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import aptActions from 'store/appt/actions';
import aptSelectors from 'store/appt/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { accentCssVars, CALLER_ID_THEME } from './accent-tokens';
import AddCustomerSheet from './AddCustomerSheet';
import { consumePendingCallerBooking } from './caller-booking-handoff';
import BookingPage from './BookingPage';
import CallListSheet from './CallListSheet';
import CallerToast from './CallerToast';
import GhostSlot from './GhostSlot';
import { PhoneIcon } from './icons';
import { ANYONE_COL, useBookingForm } from './use-booking-form';
import { CallerRef, callerRefFromPending, useCallerId } from './use-caller-id';
import './caller-id.scss';

// Caller ID overlay for OwnerCalendar: caller FAB + pending-count badge,
// pending-call sheet, in-overlay booking page + ghost slot, toast. Banners are
// app-wide (CallerBannerHost); their Book lands here via location state.
const CallerIdOverlay = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const calendarDateStr = useAppSelector(aptSelectors.date);
  const {
    pending, unhandledCount, view, toast,
    showToast, sendSms, smsLoading, refetchPending, openSheet, closeSheet, openForm, closeForm, completeBooking,
  } = useCallerId();
  const form = useBookingForm();

  // The form page stays mounted after Back (slide-out animation + ghost slot
  // persistence); it fully resets each time Book opens it for a caller.
  const [formCaller, setFormCaller] = useState<CallerRef | null>(null);
  const lastOpenedFor = useRef<string | null>(null);
  // unknown caller whose Book is waiting on the Add Customer sheet
  const [addTarget, setAddTarget] = useState<CallerRef | null>(null);

  const openBookingForm = (caller: CallerRef) => {
    if (lastOpenedFor.current !== caller.callerId) form.reset();
    lastOpenedFor.current = caller.callerId;
    setFormCaller(caller);
    openForm(caller);
  };

  // Book: unknown callers must get a customer record first (Add Customer
  // sheet); cancel keeps the call unhandled and the underlying view untouched.
  const book = (caller: CallerRef) => {
    if (caller.customerId === null) setAddTarget(caller);
    else openBookingForm(caller);
  };

  // Book on an app-wide banner (CallerBannerHost) navigates here after
  // registering the caller in the hand-off module — consume it on mount and on
  // every navigation. Dep is the location OBJECT (new reference per navigation):
  // HashRouter has no location.key, so the key alone wouldn't retrigger when
  // Book is tapped while already on /calendar.
  useEffect(() => {
    const pendingBooking = consumePendingCallerBooking();
    if (pendingBooking) book(pendingBooking);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const onCustomerCreated = (customerId: number, fullName: string) => {
    if (!addTarget) return;
    const caller: CallerRef = { ...addTarget, customerId, customerName: fullName, justCreated: true };
    setAddTarget(null);
    showToast(`Customer added: ${fullName}`);
    // backend linked the customer to the caller record — refresh list styling
    refetchPending();
    openBookingForm(caller);
  };

  const onBooked = (caller: CallerRef, summary: string) => {
    completeBooking(caller, summary);
    form.reset();
    lastOpenedFor.current = null;
    setFormCaller(null);
    // the new appointment must appear on the calendar + availability grid
    const day = moment(calendarDateStr).format('YYYY-MM-DD');
    dispatch(aptActions.getAptData.fetch(`start_time=${day}&end_time=${day}`));
  };

  // SMS from the booking form: after success, close the form like Book does
  // and drop the retained form state so the ghost slot disappears too
  const smsFromForm = (caller: CallerRef) => {
    sendSms(caller, {
      closeForm: true,
      onClosed: () => {
        form.reset();
        lastOpenedFor.current = null;
        setFormCaller(null);
      },
    });
  };

  const ghostStaffLabel = form.selectedStaff === ANYONE_COL
    ? 'Anyone'
    : form.staff.find(s => s.id === form.selectedStaff)?.label ?? '';

  return (
    <div className="cid-root" style={accentCssVars(CALLER_ID_THEME)}>
      {view === 'fab' && (
        <button type="button" className="cid-fab" onClick={openSheet}>
          <PhoneIcon size={23} />
          {unhandledCount > 0 && <span className="cid-fab-badge">{unhandledCount}</span>}
        </button>
      )}

      {view === 'list' && (
        <CallListSheet
          calls={pending}
          onSms={p => sendSms(callerRefFromPending(p))}
          onBook={p => book(callerRefFromPending(p))}
          onClose={closeSheet}
        />
      )}

      {/* ghost pending slot on the underlying calendar (form date == calendar date by design) */}
      {formCaller && form.selectedTime && view !== 'form' && (
        <GhostSlot
          startMinutes={form.selectedTime.minutes}
          durationMin={form.durationMin}
          customerName={formCaller.customerName || 'Unknown caller'}
          staffLabel={ghostStaffLabel}
        />
      )}

      {formCaller && (
        <div className={`cid-form-wrap ${view === 'form' ? 'open' : ''}`}>
          <BookingPage
            caller={formCaller}
            form={form}
            onBack={closeForm}
            onSms={smsFromForm}
            onBooked={onBooked}
            onToast={showToast}
          />
        </div>
      )}

      {addTarget && (
        <AddCustomerSheet
          key={addTarget.callerId}
          caller={addTarget}
          onCreated={onCustomerCreated}
          onClose={() => setAddTarget(null)}
          onToast={showToast}
        />
      )}

      {toast && <CallerToast message={toast} />}

      {smsLoading && <Loader />}
    </div>
  );
};

export default CallerIdOverlay;
